import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Dialog, DialogContent, Typography } from "@mui/material";
import { endOfMonth, startOfMonth } from "date-fns";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import { ItemFailedToSave, ItemToSave, Props, State } from "./interfaces";
import { DragNDropContainer, Container, AddButton } from "./styles";
import UploadedFilesList from "./UploadedFilesList";
import { DocumentCategoryRef } from "../../models/document-categories";
import { DocumentTypeRef } from "../../models/document-types";
import { DocumentRef, DocumentSTO } from "../../models/documents";
import { Period, PeriodType } from "../../models/documents/period";
import { IncomingMailSTO } from "../../models/incoming-mail/incoming-mail-sto";
import { IncomingMailStatusRef } from "../../models/incoming-mail-status/incoming-mail-status-ref";
import { MailClassificationStatusRef } from "../../models/mail-classification-status/mail-classification-status-ref";
import { IFile } from "../../reusable-components/DocumentViewer/interfaces";
import { saveDocument } from "../../services/documents";
import { IncomingMailApiService } from "../../services/incoming-mail/incoming-mail-api.service";
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_TYPES,
  DOCUMENTS_STATUS_TYPES,
  INCOMING_MAIL_STATUSES,
  MAIL_CLASSIFICATION_STATUS,
} from "../../utils/constants/constants";
import { getFileMimeType } from "../../utils/files-utils/check-file-mime-type";
import { getFileExtension } from "../../utils/files-utils/get-file-extension";
import ConfirmationChangesDialog from "../ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../DialogActions/DialogActions";
import DialogTitle from "../DialogTitle/DialogTitle";
import { INewFile } from "../Document/Document/interfaces";
import { handleUploadNewFiles } from "../Document/Document/utils";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";

const AddIncomingMail = ({ mailInbox, handleNotifyParent }: Props) => {
  const tRP = "components.add_incoming_mail";
  const { t } = useTranslation();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const DEFAULT_INCOMING_MAIL: IncomingMailSTO = useMemo(
    () => ({
      id: "",
      isDeleted: false,
      inbox: mailInbox ?? null,
      document: null,
      receivedFrom: null,
      addressedTo: null,
      subject: null,
      emailTo: null,
      status: INCOMING_MAIL_STATUSES.Pending as IncomingMailStatusRef,
      classificationStatus: MAIL_CLASSIFICATION_STATUS.ToBeClassified as MailClassificationStatusRef,
      doNotProcessByOcr: false,
    }),
    [mailInbox]
  );

  const DEFAULT_DOCUMENT: DocumentSTO = useMemo(
    () => ({
      id: "",
      companyAccount: null,
      isDeleted: false,
      name: "",
      legalEntity: null,
      category: DOCUMENT_CATEGORIES.OTHER as DocumentCategoryRef,
      type: DOCUMENT_TYPES.Other as DocumentTypeRef,
      status: DOCUMENTS_STATUS_TYPES.PENDING,
      period: {
        year: new Date().getFullYear(),
        type: PeriodType.Month,
        periodNumber: new Date().getMonth(),
        startDate: startOfMonth(new Date()),
        endDate: endOfMonth(new Date()),
      } as Period,
      documentDate: new Date(),
      published: false,
      employee: null,
      employeeGroup: null,
      client: null,
      vendor: null,
    }),
    []
  );

  const INITIAL_STATE: State = {
    open: false,
    CCDOpen: false,
    isDragging: false,
    files: [],
    rawFiles: [],
    itemsFailedToSave: [],
    attemptedToSave: false,
    loading: false,
  };

  const [state, setState] = useState({
    ...INITIAL_STATE,
  });

  //#region drag over
  /***
   * @param  {React.DragEvent} event -will be triggered when user hovers over the Dragn Drop block
   **/
  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setState((state) => ({ ...state, isDragging: true }));
  }, []);
  //#endregion

  //#region drag leave

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setState((state) => ({ ...state, isDragging: false }));
  }, []);
  //#endregion

  //#region handle files
  const handleFiles = useCallback(async (files: FileList) => {
    const newFiles: IFile[] = await Promise.all(
      [...files]
        .filter((_file) => getFileExtension(_file.name) === "pdf")
        .map(async (_file) => {
          const mimeType = await getFileMimeType(_file);
          return {
            file: new Blob([_file], { type: mimeType?.mime }),
            fileName: _file.name,
            fileExtension: getFileExtension(_file.name),
          };
        })
    );

    setState((state) => ({
      ...state,
      files: [...state.files, ...newFiles],
      rawFiles: [...state.rawFiles, ...files],
    }));
  }, []);
  //#endregion

  //#region handle drop
  const handleDrop = useCallback(async (event: React.DragEvent) => {
    event.preventDefault();
    setState((state) => ({ ...state, isDragging: false }));

    const files = event.dataTransfer.files;
    handleFiles(files);
  }, []);
  //#endregion

  //#region browse files
  const handleBrowseFiles = useCallback(() => {
    fileInputRef.current?.click();
  }, []);
  //#endregion

  //#region save doc
  const handleSaveDocument = useCallback(
    async (itemToSave: ItemToSave, correlationID: string): Promise<string | null> => {
      const response = await saveDocument(itemToSave.document, correlationID, true, true);

      if (response.error) {
        return response.error;
      }
      return null;
    },
    []
  );
  //#endregion

  //#region save file
  const handleSaveFile = useCallback(async (itemToSave: ItemToSave, correlationID: string): Promise<string | null> => {
    const fileResponse = await handleUploadNewFiles(itemToSave.document, [itemToSave.file], null, correlationID, true);
    if (fileResponse?.error) {
      return fileResponse.error;
    }
    return null;
  }, []);
  //#endregion

  //#region save incoming mail
  const handleSaveIncomingMail = useCallback(async (itemToSave: ItemToSave): Promise<string | null> => {
    const incomingMailResponse = await new IncomingMailApiService().saveItems([itemToSave.incomingMail]);
    if (incomingMailResponse.error) {
      return incomingMailResponse.error;
    }
    return null;
  }, []);
  //#endregion

  //#region handle save
  const handleSave = useCallback(
    async (itemsToSave: ItemToSave[]) => {
      const itemsFailedToSave: ItemFailedToSave[] = [];

      setState((state) => ({ ...state, loading: true }));

      const saveProcesses: Promise<void>[] = [];

      for (let i = 0; i < itemsToSave.length; i++) {
        const correlationID = uuid();
        const itemToSave = itemsToSave[i];

        const DEFAULT_FAILED_SAVING_ITEM = {
          item: itemToSave,
          documentError: "",
          fileError: "",
          incomingMailError: "",
          index: i,
        };

        const process = handleSaveDocument(itemToSave, correlationID).then((error) => {
          if (error) {
            itemsFailedToSave.push({
              ...DEFAULT_FAILED_SAVING_ITEM,
              documentError: error,
            });
          } else {
            return handleSaveFile(itemToSave, correlationID).then((error) => {
              if (error) {
                itemsFailedToSave.push({
                  ...DEFAULT_FAILED_SAVING_ITEM,
                  fileError: error,
                });
              } else {
                return handleSaveIncomingMail(itemToSave).then((error) => {
                  if (error) {
                    itemsFailedToSave.push({
                      ...DEFAULT_FAILED_SAVING_ITEM,
                      incomingMailError: error,
                    });
                  }
                });
              }
            });
          }
        });
        saveProcesses.push(process);
      }

      await Promise.all(saveProcesses).finally(() => {
        if (itemsFailedToSave.length > 0) {
          setState((state) => ({
            ...state,
            itemsFailedToSave: [...itemsFailedToSave],
            attemptedToSave: true,
            loading: false,
          }));
          handleNotifyParent();
        } else {
          setState({ ...INITIAL_STATE });
          handleNotifyParent();
        }
      });
    },
    [handleSaveDocument, handleSaveFile, handleSaveIncomingMail, INITIAL_STATE]
  );
  //#endregion

  useEffect(() => {
    console.log(state.itemsFailedToSave);
  }, [state.itemsFailedToSave]);

  //#region generate items to save
  const generateItemsToSave = useCallback(() => {
    const itemsToSave: ItemToSave[] = [];

    // generate INewFile[], this type is used to save the files later
    const filesToSave: INewFile[] = state.rawFiles.map((_file) => ({
      file: _file,
      fileName: _file.name,
    }));

    const generatingPromises: Promise<void>[] = [];

    // For each of file uploaded , we create a document
    for (let i = 0; i < filesToSave.length; i++) {
      const promise = new Promise<void>((resolve) => {
        const document = {
          ...DEFAULT_DOCUMENT,
          id: uuid(),
        };

        itemsToSave.push({
          file: filesToSave[i],
          document,
          incomingMail: {
            ...DEFAULT_INCOMING_MAIL,
            id: uuid(),
            document: document as DocumentRef,
          },
        });

        resolve();
      });

      generatingPromises.push(promise);
    }

    Promise.all(generatingPromises).then(() => handleSave(itemsToSave));
  }, [DEFAULT_DOCUMENT, DEFAULT_INCOMING_MAIL, handleSave, state.rawFiles]);
  //#endregion
  return (
    <>
      <AddButton onClick={() => setState((state) => ({ ...state, open: true }))}>
        <FontAwesomeIcon icon={faPlus} />
      </AddButton>
      <Dialog
        open={state.open}
        fullWidth
      >
        <DialogTitle
          title={`${t(`${tRP}.title`)}`}
          handleClose={() => setState((state) => ({ ...state, CCDOpen: true }))}
        />
        <DialogContent>
          <LoadingOverlay loading={state.loading}>
            <Container>
              <Alert severity="info">{`${t(`${tRP}.only_pdf`)}`}</Alert>
              <DragNDropContainer
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => handleBrowseFiles()}
                isDragging={state.isDragging}
              >
                <Typography>{`${t(`${tRP}.drag_and_drop`)}`}</Typography>
                <Typography>{`${t(`${tRP}.or_click_to_upload`)}`}</Typography>
              </DragNDropContainer>
              <input
                style={{ display: "none" }}
                type="file"
                ref={fileInputRef}
                onChange={(e) => e.currentTarget.files && handleFiles(e.currentTarget.files)}
                multiple
                accept=".pdf"
              />
              <UploadedFilesList
                files={state.files}
                saveErrors={state.itemsFailedToSave.map((itemFailedToSave) => ({
                  index: itemFailedToSave.index,
                  saveDocumentError: itemFailedToSave.documentError,
                  saveFileError: itemFailedToSave.fileError,
                  saveIncomingMailError: itemFailedToSave.incomingMailError,
                }))}
                attemptedToSave={state.attemptedToSave}
              />
            </Container>
          </LoadingOverlay>
        </DialogContent>
        <DialogActions
          handleCancel={() => setState((state) => ({ ...state, CCDOpen: true }))}
          handleSave={generateItemsToSave}
        />
      </Dialog>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((state) => ({ ...state, CCDOpen: false }))}
        handleDiscard={() => setState({ ...INITIAL_STATE })}
      />
    </>
  );
};

export default AddIncomingMail;
